/* VELISÉ — Shop landing page.

   Fünf Dinge: Aufdecken beim Scrollen, die Tagline Wort für Wort, der
   Vorher/Nachher-Regler, die mitlaufende Kaufleiste und das Legen in den
   Warenkorb ohne Seitenwechsel.

   Durchgehend IntersectionObserver statt eines Scroll-Listeners: ein
   Listener auf window.scroll liest pro Ereignis Positionen zurück,
   erzwingt Layout und kostet auf dem Handy Bilder pro Sekunde.

   Der Regler folgt dem Finger 1:1 und rechnet beim Loslassen den Schwung
   weiter, statt hart zu stoppen. Jede Bewegung lässt sich mitten im Lauf
   greifen und umdrehen, weil die Feder immer vom aktuell gezeichneten
   Wert startet, nicht vom Ziel. */
(function () {
  'use strict';

  var EASE_RESPONSE = 0.4;
  var EASE_DAMPING = 1.0;

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  /* Je weiter über die Kante gezogen wird, desto weniger folgt der Regler.
     Echte Dinge werden langsamer, bevor sie stehen bleiben. */
  function rubberband(overshoot, dimension, constant) {
    constant = constant || 0.55;
    return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
  }

  /* Apples Projektion aus "Designing Fluid Interfaces": exponentielle
     Abklingkurve, nicht die Lehrbuchformel. */
  function project(velocity, rate) {
    rate = rate || 0.998;
    return (velocity / 1000) * rate / (1 - rate);
  }

  /* Gedämpfter Federschwinger, in requestAnimationFrame integriert.
     Gibt eine Abbruchfunktion zurück, damit eine neue Geste die laufende
     Bewegung sofort übernehmen kann. */
  function spring(from, to, velocity, opts, onUpdate) {
    var response = (opts && opts.response) || EASE_RESPONSE;
    var damping = (opts && opts.damping) || EASE_DAMPING;
    var k = Math.pow((2 * Math.PI) / response, 2);
    var c = (4 * Math.PI * damping) / response;

    var x = from - to;
    var v = velocity || 0;
    var last = 0;
    var raf = 0;
    var stopped = false;

    function tick(now) {
      if (stopped) return;
      if (!last) last = now;
      var dt = Math.min((now - last) / 1000, 1 / 30);
      last = now;

      var a = -k * x - c * v;
      v += a * dt;
      x += v * dt;

      if (Math.abs(x) < 0.4 && Math.abs(v) < 6) {
        onUpdate(to);
        return;
      }
      onUpdate(to + x);
      raf = window.requestAnimationFrame(tick);
    }

    raf = window.requestAnimationFrame(tick);
    return function cancel() { stopped = true; window.cancelAnimationFrame(raf); };
  }

  /* ---------- 1. Aufdecken beim Scrollen ---------- */

  function initReveal(root) {
    var els = root.querySelectorAll('[data-r]');
    if (!els.length) return;

    if (reduced() || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px' });

    Array.prototype.forEach.call(els, function (el, i) {
      /* Gestaffelt innerhalb einer Reihe, damit Karten nacheinander
         ankommen statt als Block aufzublinken. */
      el.style.transitionDelay = ((i % 3) * 80) + 'ms';
      io.observe(el);
    });

    /* Wer die Seite mitten drin betritt, soll nicht auf leere Kästen sehen. */
    window.requestAnimationFrame(function () {
      Array.prototype.forEach.call(els, function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-in');
      });
    });
  }

  /* ---------- 2. Tagline, Wort für Wort ---------- */

  function initTagline(root) {
    var blocks = root.querySelectorAll('[data-vsl-tagline]');
    if (!blocks.length) return;

    Array.prototype.forEach.call(blocks, function (block) {
      var words = block.querySelectorAll('.vsl-tag__w');
      if (!words.length) return;

      if (reduced() || !('IntersectionObserver' in window)) {
        Array.prototype.forEach.call(words, function (w) { w.classList.add('is-on'); });
        return;
      }

      /* Die Auslöselinie liegt bei etwa 62% der Fensterhöhe: das Wort
         schaltet, kurz bevor der Blick es erreicht. */
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-on');
          io.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -38% 0px', threshold: 0 });

      Array.prototype.forEach.call(words, function (w) { io.observe(w); });

      window.requestAnimationFrame(function () {
        Array.prototype.forEach.call(words, function (w) {
          if (w.getBoundingClientRect().top < window.innerHeight * 0.62) {
            w.classList.add('is-on');
            io.unobserve(w);
          }
        });
      });
    });
  }

  /* ---------- 3. Vorher / Nachher ---------- */

  function initCompare(root) {
    var nodes = root.querySelectorAll('[data-vsl-ba]');

    Array.prototype.forEach.call(nodes, function (el) {
      var width = el.getBoundingClientRect().width || 1;
      var pos = width / 2;
      var cancel = null;
      var dragging = false;
      var grabOffset = 0;
      var history = [];

      function paint(px) {
        pos = px;
        var pct = clamp((px / width) * 100, 0, 100);
        el.style.setProperty('--ba', pct + '%');
        el.setAttribute('aria-valuenow', Math.round(pct));
      }

      function measure() {
        var next = el.getBoundingClientRect().width || 1;
        var ratio = pos / width;
        width = next;
        paint(ratio * width);
      }

      /* Beim Ziehen wird der Wert direkt gesetzt, nicht animiert: Inhalt
         und Finger bewegen sich zusammen, sonst bricht die Illusion. */
      function onDown(e) {
        if (cancel) { cancel(); cancel = null; }
        width = el.getBoundingClientRect().width || 1;

        var x = e.clientX - el.getBoundingClientRect().left;
        /* Direkt am Griff bleibt der Abstand erhalten, mit dem gegriffen
           wurde. Weiter weg ist es ein Tippen: der Regler springt hin. */
        grabOffset = Math.abs(x - pos) <= 32 ? x - pos : 0;

        dragging = true;
        history = [{ x: x, t: e.timeStamp }];
        el.classList.add('is-grabbed');
        if (el.setPointerCapture) el.setPointerCapture(e.pointerId);
        if (grabOffset === 0) paint(x);
      }

      function onMove(e) {
        if (!dragging) return;
        e.preventDefault();
        var x = e.clientX - el.getBoundingClientRect().left;

        history.push({ x: x, t: e.timeStamp });
        if (history.length > 5) history.shift();

        var raw = x - grabOffset;
        /* Über der Kante federt es, statt anzuschlagen. */
        if (raw < 0) raw = rubberband(raw, width);
        else if (raw > width) raw = width + rubberband(raw - width, width);
        paint(raw);
      }

      function onUp(e) {
        if (!dragging) return;
        dragging = false;
        el.classList.remove('is-grabbed');
        if (el.releasePointerCapture && el.hasPointerCapture && el.hasPointerCapture(e.pointerId)) {
          el.releasePointerCapture(e.pointerId);
        }

        /* Geschwindigkeit aus den letzten Bewegungen, nicht aus dem
           letzten Punkt allein. */
        var v = 0;
        if (history.length > 1) {
          var a = history[0];
          var b = history[history.length - 1];
          var dt = (b.t - a.t) / 1000;
          if (dt > 0) v = (b.x - a.x) / dt;
        }

        if (reduced()) { paint(clamp(pos, 0, width)); return; }

        /* Erst dorthin zielen, wo die Geste hinwill, dann den Schwung
           an die Feder übergeben — so gibt es keine Naht zwischen
           Ziehen und Animieren. */
        var target = clamp(pos + project(v), 0, width);
        var bounce = Math.abs(v) > 200 ? 0.8 : 1.0;
        cancel = spring(pos, target, v, { response: 0.4, damping: bounce }, paint);
      }

      function onKey(e) {
        var step = e.shiftKey ? width * 0.1 : width * 0.02;
        var next = null;
        if (e.key === 'ArrowLeft') next = pos - step;
        else if (e.key === 'ArrowRight') next = pos + step;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = width;
        if (next === null) return;
        e.preventDefault();
        if (cancel) { cancel(); cancel = null; }
        paint(clamp(next, 0, width));
      }

      el.addEventListener('pointerdown', onDown);
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerup', onUp);
      el.addEventListener('pointercancel', onUp);
      el.addEventListener('keydown', onKey);

      if ('ResizeObserver' in window) new ResizeObserver(measure).observe(el);
      else window.addEventListener('resize', measure);

      paint(pos);
    });
  }

  /* ---------- 4. UGC-Video ----------

     Das Video läuft von allein: stumm, in der Schleife, ohne Bedienung.
     Ein Klick auf Play ist eine Hürde vor dem Beweis. Wer die Seite
     überfliegt, sieht das Gerät jetzt in Bewegung, ohne etwas zu tun.

     Stumm ist dabei keine Geschmacksfrage, sondern Voraussetzung: Browser
     lassen Ton erst nach einer Geste zu. Ein Video mit Ton würde gar
     nicht erst starten.

     Es spielt nur, solange es im Bild ist. Ein Video, das unsichtbar
     weiterläuft, kostet Akku und Daten und zeigt niemandem etwas.

     Wer Bewegung abbestellt hat, bekommt sie nicht aufgedrängt: dann
     bleibt das Standbild stehen und das Video behält seine Bedienung. */

  function initVideo(root) {
    var frames = root.querySelectorAll('.vsl-video__frame');
    var quiet = reduced();

    Array.prototype.forEach.call(frames, function (frame) {
      if (frame.__vslVideo) return;
      frame.__vslVideo = true;

      /* Das Einbetten lag bisher hinter dem Knopf. Jetzt passiert es
         sofort; das Standbild bleibt darunter liegen und deckt den
         Rahmen, solange das erste Bild noch lädt. */
      var tpl = frame.querySelector('template[data-vsl-embed]');
      if (tpl) {
        var btn = frame.querySelector('[data-vsl-play]');
        if (btn) btn.remove();
        frame.appendChild(tpl.content.cloneNode(true));
      }

      /* YouTube und Vimeo brauchen den stummen Zustand in der Adresse,
         sonst blockt der Browser den Start. Die Schleife ebenfalls. */
      var ifr = frame.querySelector('iframe');
      if (ifr) {
        var src = ifr.getAttribute('src') || '';
        if (quiet) {
          ifr.setAttribute('src', src.replace('autoplay=1', 'autoplay=0'));
        } else if (src.indexOf('youtube') > -1) {
          var id = src.split('/embed/')[1];
          id = id ? id.split('?')[0] : '';
          ifr.setAttribute('src', src + '&mute=1&loop=1&controls=0&modestbranding=1&playlist=' + id);
        } else if (src.indexOf('vimeo') > -1) {
          ifr.setAttribute('src', src + '&muted=1&loop=1&background=1');
        }
      }

      var v = frame.querySelector('video');
      if (!v) return;

      if (quiet) { v.controls = true; return; }

      /* Attribut und Eigenschaft: Safari liest beim Start das Attribut,
         alles andere die Eigenschaft. */
      v.muted = true;
      v.defaultMuted = true;
      v.loop = true;
      v.controls = false;
      v.playsInline = true;
      v.setAttribute('muted', '');
      v.setAttribute('loop', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
      v.removeAttribute('controls');

      function play() {
        var p = v.play();
        if (p && p['catch']) p['catch'](function () {});
      }

      /* Sicherheitsnetz: schließt ein Gerät die Schleife nicht von selbst,
         springt sie hier zurück, statt am letzten Bild stehenzubleiben. */
      v.addEventListener('ended', function () {
        try { v.currentTime = 0; } catch (err) {}
        play();
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) play();
            else v.pause();
          });
        }, { threshold: 0.2 }).observe(v);
      }

      /* Erster Versuch sofort, zweiter sobald Bilddaten da sind: manche
         Browser lehnen den Start vor dem ersten Frame noch ab. */
      play();
      v.addEventListener('loadeddata', play);
      v.addEventListener('canplay', play);
    });
  }

  /* ---------- 5. Mitlaufende Kaufleiste ---------- */

  function initBar(root) {
    var bar = root.querySelector('[data-vsl-bar]');
    if (!bar || !('IntersectionObserver' in window)) return;

    var top = root.querySelector('[data-vsl-sentinel="top"]');
    var end = root.querySelector('[data-vsl-sentinel="end"]');
    if (!top) return;

    var pastHero = false;
    var atFinal = false;

    function sync() {
      bar.classList.toggle('is-up', pastHero && !atFinal);
    }

    /* Die Leiste kommt erst, wenn der Hero-Knopf oben aus dem Bild ist,
       und geht wieder, sobald der Schlusscall sichtbar wird: zwei Knöpfe
       für dieselbe Handlung nebeneinander kosten nur Aufmerksamkeit. */
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        pastHero = !e.isIntersecting && e.boundingClientRect.top < 0;
        sync();
      });
    }, { threshold: 0 }).observe(top);

    if (end) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { atFinal = e.isIntersecting; sync(); });
      }, { threshold: 0 }).observe(end);
    }
  }

  /* ---------- 6. In den Warenkorb ----------

     Ein Tipp legt genau einen Artikel in den Korb.

     Bisher war nur der gedrückte Knopf gesperrt, und nur solange die
     Anfrage lief. Danach stand "Added to cart" bis zur Weiterleitung auf
     dem Knopf und nahm weiter Tipps an, Kaufleiste und Karten ohnehin.
     Wer in dieser Zeit noch einmal tippte, hatte das Gerät doppelt im
     Korb. Und riss die Verbindung ab, nachdem Shopify den Artikel schon
     gebucht hatte, schickte der Fehlerzweig dasselbe Formular noch einmal.

     Jetzt gilt eine Sperre für die ganze Seite, vom ersten Tipp bis die
     Warenkorbseite geladen ist. Sechs Formulare legen dasselbe Gerät in
     den Korb; eine Sperre pro Knopf reicht dafür nicht. */

  var cart = { busy: false, btn: null, label: '' };

  function unlockCart() {
    cart.busy = false;
    var btn = cart.btn;
    cart.btn = null;
    if (!btn) return;
    btn.classList.remove('is-busy', 'is-done');
    btn.removeAttribute('aria-busy');
    btn.removeAttribute('aria-disabled');
    btn.innerHTML = cart.label;
  }

  /* Mit der Zurück-Taste holt der Browser die Seite oft unverändert aus
     dem Speicher: grüner Knopf, Sperre noch aktiv. Beides zurücksetzen,
     sonst ließe sich nichts mehr in den Korb legen. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) unlockCart();
  });

  function goToCart(form) {
    window.location.href = form.getAttribute('data-cart-url') || '/cart';
  }

  function initCart(root) {
    var forms = root.querySelectorAll('form[data-vsl-add]');

    Array.prototype.forEach.call(forms, function (form) {
      form.addEventListener('submit', function (e) {
        if (!window.fetch) return; /* Ohne fetch übernimmt der normale Post. */
        e.preventDefault();
        if (cart.busy) return; /* Es ist schon ein Artikel unterwegs. */

        var btn = form.querySelector('button[type="submit"]');
        cart.busy = true;
        cart.btn = btn;
        cart.label = btn ? btn.innerHTML : '';
        if (btn) {
          btn.classList.add('is-busy');
          btn.setAttribute('aria-busy', 'true');
          btn.setAttribute('aria-disabled', 'true');
        }

        /* Hängt die Anfrage, ist nach 15 Sekunden unklar, ob der Artikel
           angekommen ist. Dann zeigt die Warenkorbseite den echten Stand. */
        var ctrl = window.AbortController ? new AbortController() : null;
        var hang = ctrl ? window.setTimeout(function () { ctrl.abort(); }, 15000) : 0;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: new FormData(form),
          signal: ctrl ? ctrl.signal : undefined
        })
          .then(function (res) {
            window.clearTimeout(hang);
            if (!res.ok) {
              var err = new Error('add failed');
              err.status = res.status;
              throw err;
            }
            /* Gebucht ist gebucht, auch wenn sich die Antwort nicht lesen
               lässt. Bisher lief das in den Fehlerzweig und damit in einen
               zweiten Post. */
            return res.json()['catch'](function () { return {}; });
          })
          .then(function (data) {
            if (btn) {
              btn.classList.remove('is-busy');
              btn.classList.add('is-done');
              btn.removeAttribute('aria-busy');
              btn.innerHTML = 'Added to cart';
            }

            /* Horizon hört auf das Standardereignis und öffnet dann seine
               Schublade. Findet sich danach keine offene Schublade, geht
               es auf die Warenkorbseite — der Artikel liegt in jedem Fall
               drin, der Kunde soll das nur auch sehen. */
            var ev = new CustomEvent('cart:lines-update', { bubbles: true, cancelable: false });
            ev.action = 'add';
            ev.data = data;
            ev.source = 'velise-shop';
            ev.promise = Promise.resolve({ detail: { didError: false } });
            form.dispatchEvent(ev);

            window.setTimeout(function () {
              if (!document.querySelector('dialog[open] cart-drawer-component, cart-drawer-component dialog[open]')) {
                /* Die Sperre bleibt, bis die Warenkorbseite da ist. */
                goToCart(form);
                return;
              }
              unlockCart();
            }, 700);
          })
          ['catch'](function (err) {
            window.clearTimeout(hang);
            if (btn) {
              btn.classList.remove('is-busy');
              btn.removeAttribute('aria-busy');
              btn.innerHTML = cart.label;
            }

            /* 4xx: Shopify hat abgelehnt, im Korb liegt nichts. Kein Alert,
               kein "Ups": der normale Post übernimmt und liefert Shopifys
               eigene Fehlermeldung. */
            if (err && err.status >= 400 && err.status < 500) {
              form.submit();
              return;
            }

            /* Keine Antwort, Abbruch oder Serverfehler: Der Artikel kann
               trotzdem angekommen sein, ein zweiter Post würde ihn doppelt
               buchen. Die Warenkorbseite zeigt, was wirklich drin liegt. */
            goToCart(form);
          });
      });
    });
  }

  /* ---------- Start ---------- */

  function boot() {
    var roots = document.querySelectorAll('.vsl');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__vslDone) return;
      root.__vslDone = true;
      initReveal(root);
      initTagline(root);
      initCompare(root);
      initVideo(root);
      initBar(root);
      initCart(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Im Theme-Editor wird die Sektion neu gezeichnet; der neue Knoten
     trägt die Markierung nicht und wird frisch verdrahtet. */
  document.addEventListener('shopify:section:load', boot);
})();
